import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { LakehouseConnectionInput, LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { GetLakehouseTablesDocument, ListS3FilesDocument } from '@vendia/control-plane-api'
import { useNavigate } from 'react-router'
import Card, { CardRule } from 'src/components/containers/card'
import RadioGroupField from 'src/components/fields/radio-group.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { isRequiredValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useGetLakehouseConnections } from 'src/utils/hooks/use-get-lakehouse-connections'
import { filterS3IngestionFiles } from 'src/utils/lakehouse/connection'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import notify from 'src/utils/notify'

import { StepId } from '../config'

export const StepSelectConnection: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const { connectionId, nodeName, uniName } = useStore(form.store, (s) => s.values)
  const useGetLakehouseConnectionsResponse = useGetLakehouseConnections()
  const connections = useGetLakehouseConnectionsResponse.data?.connections ?? []
  const connectionOptions = connections.map((connection: LakehouseConnectionInput) => ({
    value: connection.id!,
    label: connection.name!,
  }))
  const client = useControlPlaneApi()
  const navigate = useNavigate()

  const handleConnectionChange = async (connectionId?: string, previousConnectionId?: string) => {
    if (previousConnectionId && previousConnectionId !== connectionId) {
      const confirm = window.confirm(
        'Changing the connection will reset any table and column/row policy selections. Are you sure you want to continue?',
      )

      if (!confirm) {
        return
      }
      form.setFieldValue('source.availableTables', [])
      form.setFieldValue('source.connection', undefined)
      form.setFieldValue('source.snowflake', undefined)
      form.setFieldValue('source.cloudera', undefined)
      form.setFieldValue('source.databricks', undefined)
      form.setFieldValue('source.postgres', undefined)
      form.setFieldValue('source.mysql', undefined)
      form.setFieldValue('source.bigquery', undefined)
      form.setFieldValue('source.selectedTable', '')
    }
  }

  const getTablesMutation = useMutation({
    mutationFn: () => {
      const selectedConnection = connections.find(
        (connection: LakehouseConnectionInput) => connection.id === connectionId,
      )
      if (!selectedConnection) {
        notify.error('Error fetching tables: Connection not found')
        return Promise.reject(new Error('Connection not found'))
      }
      form.setFieldValue('source.connection', selectedConnection.type)

      switch (selectedConnection.type) {
        case LakehouseConnectionTypeEnum.Snowflake:
          if (selectedConnection.snowflakeConnection) {
            form.setFieldValue('source.snowflake', selectedConnection.snowflakeConnection)
          }
          break
        case LakehouseConnectionTypeEnum.Cloudera:
          if (selectedConnection.clouderaConnection) {
            form.setFieldValue('source.cloudera', selectedConnection.clouderaConnection)
          }
          break
        case LakehouseConnectionTypeEnum.Postgres:
          if (selectedConnection.postgresConnection) {
            form.setFieldValue('source.postgres', selectedConnection.postgresConnection)
          }
          break
        case LakehouseConnectionTypeEnum.Mysql:
          if (selectedConnection.mysqlConnection) {
            form.setFieldValue('source.mysql', selectedConnection.mysqlConnection)
          }
          break
        case LakehouseConnectionTypeEnum.Databricks:
          if (selectedConnection.databricksConnection) {
            form.setFieldValue('source.databricks', selectedConnection.databricksConnection)
          }
          break
        case LakehouseConnectionTypeEnum.Bigquery:
          if (selectedConnection.bigqueryConnection) {
            form.setFieldValue('source.bigquery', selectedConnection.bigqueryConnection)
          }
          break
      }

      return client.graphqlFetch({
        query: GetLakehouseTablesDocument,
        variables: {
          uniName: uniName!,
          nodeName: nodeName!,
          connectionInput: {
            id: connectionId,
            type: selectedConnection.type,
            clouderaConnection: selectedConnection.clouderaConnection,
            snowflakeConnection: selectedConnection.snowflakeConnection,
            postgresConnection: selectedConnection.postgresConnection,
            mysqlConnection: selectedConnection.mysqlConnection,
            databricksConnection: selectedConnection.databricksConnection,
            bigqueryConnection: selectedConnection.bigqueryConnection,
          },
        },
      })
    },
    onSuccess: (response) => {
      form.setFieldValue('source.availableTables', response.getLakehouseTables.tables as string[])
      context.goToStep(StepId.SelectTable)
    },
    onError: (error) => notify.error(`Error fetching tables with connection: ${error}`),
  })

  const getFilesMutation = useMutation({
    mutationFn: () => {
      const selectedConnection = connections.find(
        (connection: LakehouseConnectionInput) => connection.id === connectionId,
      )
      const s3 = selectedConnection?.s3Connection
      form.setFieldValue('source.connection', selectedConnection?.type)
      form.setFieldValue('source.s3', {
        bucketRegion: s3?.bucketRegion || '',
        bucketName: s3?.bucket || '',
        crossAccountRoleArn: s3?.crossAccountRoleArn || '',
      })

      return client.graphqlFetch({
        query: ListS3FilesDocument,
        variables: {
          input: {
            uniName: uniName!,
            nodeName: nodeName!,
            bucket: s3?.bucket!,
            bucketRegion: s3?.bucketRegion!,
            connectionInput: {
              id: connectionId,
              type: selectedConnection?.type!,
              s3Connection: s3,
            },
          },
        },
      })
    },
    onError: (error) => notify.error(`Error fetching S3 files: ${error}`),
    onSuccess: (response) => {
      const filteredFiles = filterS3IngestionFiles(response.listS3Files.objects)
      form.setFieldValue('source.availableFiles', filteredFiles)
      context.goToStep(StepId.SelectS3File)
    },
  })

  const handleNext = async () => {
    const validate = await form.validateField('connectionId', 'blur')
    if (validate?.length) {
      return
    }
    const selectedConnection = connections.find(
      (connection: LakehouseConnectionInput) => connection.id === connectionId,
    )
    if (selectedConnection?.type === 'S3') {
      getFilesMutation.mutate()
    } else {
      getTablesMutation.mutate()
    }
  }

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Select an existing connection'
        description='Select an existing connection already configured in this project.'
      />
      <ScrollableStepContent inset autoHeight>
        <Card className='mx-8 w-full max-w-4xl' childrenWrapperClassName='flex flex-col gap-4'>
          <span className='w-full text-lg font-bold capitalize'>Existing connections</span>
          <CardRule className='-mx-10' />
          <RadioGroupField
            name='connectionId'
            options={connectionOptions}
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.SelectConnection,
              validator: isRequiredValidator(),
            })}
            onChange={handleConnectionChange}
            form={form}
          />
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={() => []}>
        {() => (
          <StepButtons
            context={context}
            isSubmitting={getTablesMutation.isPending}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            nextBlocked={getTablesMutation.isPending}
            onNext={() => handleNext()}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
